import React, { useState } from 'react';
import Card from 'components/Card';
import { useNotifications } from 'context/NotificationContext';
import { markNotificationAsRead } from 'api/apiHelper';
import { toast } from 'react-toastify';

const NotificationsPage: React.FC = () => {
  const { notifications, unreadCount, loading, refreshNotifications } = useNotifications();
  const [filter, setFilter] = useState<'all' | 'unread'>('unread');
  // Ensure notifications is always an array
  const safeNotifications = Array.isArray(notifications) ? notifications : [];

  const filteredNotifications = filter === 'all' 
    ? safeNotifications 
    : safeNotifications.filter(n => !n.is_read);

  const handleMarkAsRead = async (id: number) => {
    try {
      await markNotificationAsRead(id);
      refreshNotifications();
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Failed to mark as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = safeNotifications.filter(n => !n.is_read);
      await Promise.all(unreadNotifications.map(n => markNotificationAsRead(n.id)));
      refreshNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  const getLevelStyles = (level: string) => {
    const styles = {
      info: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
      success: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700',
      danger: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
    };
    return styles[level as keyof typeof styles] || styles.info;
  };

  const getLevelIcon = (level: string) => {
    const icons = {
      info: 'fa-info-circle',
      success: 'fa-check-circle',
      warning: 'fa-exclamation-triangle',
      danger: 'fa-exclamation-circle',
    };
    return icons[level as keyof typeof icons] || icons.info;
  };

  if (loading && safeNotifications.length === 0) {
    return (
      <div className="mt-5 w-full">
        <Card extra="w-full h-full p-4">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-5 w-full">
      <Card extra="w-full h-full p-4">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                Notifications
              </h4>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {unreadCount > 0 ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
            
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="px-4 py-2 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-500 border border-brand-500 dark:border-brand-400 rounded-lg hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
                >
                  <i className="fas fa-check-double mr-2"></i>
                  Mark All Read
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filter === 'all'
                  ? 'border-brand-500 text-brand-500 dark:border-brand-400 dark:text-brand-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              All ({safeNotifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filter === 'unread'
                  ? 'border-brand-500 text-brand-500 dark:border-brand-400 dark:text-brand-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-12">
              <i className="fas fa-bell-slash text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                  !notification.is_read 
                    ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800' 
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Level Badge */}
                  <div className={`p-3 rounded-lg border ${getLevelStyles(notification.level)}`}>
                    <i className={`fas ${getLevelIcon(notification.level)} text-xl`}></i>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h5 className="font-semibold text-lg text-gray-900 dark:text-white">
                        {notification.title}
                      </h5>
                      {!notification.is_read && (
                        <span className="w-3 h-3 bg-blue-500 rounded-full flex-shrink-0 mt-1"></span>
                      )}
                    </div>
                    
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      {notification.message}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        <i className="far fa-clock mr-1"></i>
                        {new Date(notification.created_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>

                      <div className="flex">
                        {!notification.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className=" py-1 text-lg text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-500 rounded-lg transition-colors"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default NotificationsPage;